import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { IconActionButton } from '@/components/patterns/admin-page'
import {
  FloristStatusBadge,
  getFloristStatusLabel,
} from '@/components/organizations/FloristStatusBadge'
import { FloristLifecycleDialog } from '@/components/organizations/FloristLifecycleDialog'
import { createIndexTableExportColumn } from '@/components/tables/indexTableExport'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import {
  resolveOrganizationDeletionRedirectId,
  resolveOrganizationSwitchPath,
} from '@/lib/organization-switch'
import { useDeleteOrganizationMutation } from '@/lib/services/organizations/organizations.query'
import { isSalesDemoProvisioning } from '@/lib/services/sales-demos/sales-demos.utils'
import { cn } from '@/lib/utils'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { useCopyToClipboard } from '@uidotdev/usehooks'
import {
  ArrowDownAZ,
  BadgeCheck,
  CalendarClock,
  CircleOff,
  Check,
  Copy,
  Drama,
  Eye,
  Globe2,
  Mail,
  MapPin,
  MoreVerticalIcon,
  Network,
  Phone,
  Trash,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { DataTableColumnHeader } from '../DataTableColumnHeader'

function formatDefaultLocation(defaultLocation) {
  const city = defaultLocation?.city?.trim()
  const state = defaultLocation?.state?.trim()

  if (city && state) return `${city}, ${state}`
  if (city) return city
  if (state) return state

  return ''
}

function getOrganizationStatusText(organization) {
  return getFloristStatusLabel(organization.florist_status)
}

function formatOrganizationLifecycleDate(timestamp, timezone) {
  if (!timestamp) return ''

  const date = dayjs.utc(timestamp).tz(timezone)

  return date.isValid() ? date.format('YYYY-MM-DD') : ''
}

function FsnStatusIndicator({ clientId }) {
  const isEnabled =
    clientId !== null && clientId !== undefined && String(clientId).trim().length > 0

  if (!isEnabled) return null

  const tooltipText = `FSN Client ID: ${clientId}`

  return (
    <div className="flex justify-center">
      <Tooltip>
        <TooltipTrigger aria-label={tooltipText}>
          <Check aria-hidden="true" className="text-status-success-foreground" />
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

function OrganizationImpersonationLink({ targetOrganization }) {
  const { isImpersonating, user, organization: currentOrganization } = useOrganizationContext()
  const pathname = useLocation({
    select: (location) => location.pathname,
  })
  const path = usePathHelper()

  if (Number(targetOrganization.id) === 1) {
    return <span aria-hidden="true" className="size-8 shrink-0" />
  }

  const isImpersonatingThisOrganization =
    isImpersonating && Number(currentOrganization?.id) === Number(targetOrganization.id)
  const nextOrganizationId = isImpersonatingThisOrganization
    ? user?.organization_id
    : targetOrganization.id
  const switchPath = nextOrganizationId
    ? resolveOrganizationSwitchPath({
        currentPathname: pathname,
        organizationId: nextOrganizationId,
        pathHelper: path,
      })
    : null

  if (!switchPath) {
    return <span aria-hidden="true" className="size-8 shrink-0" />
  }

  const label = isImpersonatingThisOrganization
    ? `Stop impersonating ${targetOrganization.name}`
    : `Impersonate ${targetOrganization.name}`

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={switchPath}
          search={(oldSearch) => ({ ...oldSearch, page: 1 })}
          aria-label={label}
          data-index-table-drag-scroll-ignore
          className={cn(
            'inline-flex size-8 shrink-0 items-center justify-center rounded-md transition-all outline-none active:opacity-80 focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            isImpersonatingThisOrganization
              ? 'gradient-hover-button text-primary-foreground shadow-xs hover:text-primary-foreground'
              : 'text-muted-foreground hover:bg-accentSecondary hover:text-accentSecondary-foreground dark:hover:bg-accentSecondary/50'
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <Drama aria-hidden="true" className="size-4" />
        </Link>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

function OrganizationNameCell({ organization, emphasized = false }) {
  const path = usePathHelper()

  return (
    <div className="flex min-w-0 items-center gap-2">
      <OrganizationImpersonationLink targetOrganization={organization} />
      <Link {...path.organizations.show(organization.id).link} className="min-w-0">
        <Typography
          variant={emphasized ? 'body' : 'label'}
          weight={emphasized ? 'semibold' : 'inherit'}
          interaction="hoverUnderline"
          className="block truncate"
        >
          {organization.name}
        </Typography>
      </Link>
    </div>
  )
}

export const organizationColumns = [
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    enableSorting: true,
    meta: {
      headClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => <OrganizationNameCell organization={row.original} />,
  },
  {
    accessorKey: 'florist_status',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    enableSorting: false,
    meta: {
      headClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => <FloristStatusBadge status={row.original.florist_status} />,
  },
  {
    accessorKey: 'primary_domain',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Primary Domain" />,
    enableSorting: true,
    meta: {
      headClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => <span>{row.original.primary_domain || ''}</span>,
  },
  {
    accessorKey: 'fsn_client_id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="FSN" className="justify-center" />
    ),
    enableSorting: true,
    meta: {
      headClassName: 'w-[72px] whitespace-nowrap text-center',
      cellClassName: 'w-[72px] whitespace-nowrap text-center',
    },
    cell: ({ row }) => <FsnStatusIndicator clientId={row.original.fsn_client_id} />,
  },
  {
    accessorKey: 'default_location.phone',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Phone" />,
    meta: {
      headClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: function PhoneCell({ row }) {
      const { formatPhone } = usePhoneFormatter()
      const phone = row.original.default_location?.phone

      return <span>{phone ? formatPhone(phone) : ''}</span>
    },
  },
  {
    accessorKey: 'default_location.email',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    meta: {
      headClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => <span>{row.original.default_location?.email || ''}</span>,
  },
  {
    accessorKey: 'default_location.city',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Default Location" />,
    meta: {
      headClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => <span>{formatDefaultLocation(row.original.default_location)}</span>,
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date Created" />,
    meta: {
      headClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => dayjs(row.original.created_at).format('MM/DD/YYYY'),
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row }) {
      const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
      const [isLifecycleDialogOpen, setIsLifecycleDialogOpen] = useState(false)
      const [, copyToClipboard] = useCopyToClipboard()
      const path = usePathHelper()
      const navigate = useNavigate()
      const { isImpersonating, organization: currentOrganization, user } = useOrganizationContext()
      const deleteRedirectOrganizationId = resolveOrganizationDeletionRedirectId({
        deletedOrganizationId: row.original.id,
        currentOrganizationId: currentOrganization?.id,
        isImpersonating,
        userOrganizationId: user?.organization_id,
      })
      const deleteMutation = useDeleteOrganizationMutation({
        cleanupOnSuccess: !deleteRedirectOrganizationId,
      })

      const handleDeleteClick = (e) => {
        e.preventDefault()
        setIsDeleteDialogOpen(true)
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
      }

      const handleCopyStripeAccountId = async (e) => {
        e.preventDefault()
        try {
          await copyToClipboard(row.original.stripe_account_id)
          toast.success('Stripe Account ID copied to clipboard')
        } catch {
          toast.error('Failed to copy to clipboard')
        }
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
      }

      const isMarkLost = row.original.florist_status === 'live'
      const lifecycleAction = isMarkLost ? 'Mark Lost' : 'Mark Live'
      const LifecycleIcon = isMarkLost ? CircleOff : BadgeCheck

      const handleLifecycleClick = (e) => {
        e.preventDefault()
        setIsLifecycleDialogOpen(true)
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
      }

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconActionButton
                icon={MoreVerticalIcon}
                label="Open organization actions"
                variant="ghost"
                className="size-8 text-muted-foreground data-[state=open]:bg-muted"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" size="xs">
              <Can I="view" the="organizations">
                <DropdownMenuItem asChild>
                  <Link {...path.organizations.show(row.original.id).link}>
                    <Eye />
                    View
                  </Link>
                </DropdownMenuItem>
              </Can>
              {row.original?.stripe_account_id && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleCopyStripeAccountId}>
                    <Copy />
                    Copy Stripe Account ID
                  </DropdownMenuItem>
                </>
              )}
              {row.original.type === 'florist' && !row.original.sales_demo && (
                <Can I="access" the="global">
                  <Can I="edit" the="organizations">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant={isMarkLost ? 'destructive' : 'default'}
                      onClick={handleLifecycleClick}
                    >
                      <LifecycleIcon className="text-inherit" />
                      {lifecycleAction}
                    </DropdownMenuItem>
                  </Can>
                </Can>
              )}
              {row.original.id !== 1 && !isSalesDemoProvisioning(row.original.sales_demo) && (
                <Can I="delete" the="organizations">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={handleDeleteClick}>
                    <Trash className="text-inherit" />
                    Delete
                  </DropdownMenuItem>
                </Can>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Organization</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this organization? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    await deleteMutation.mutateAsync(row.original.id)
                    setIsDeleteDialogOpen(false)

                    if (deleteRedirectOrganizationId) {
                      await navigate({
                        ...path.organizations.index().using({ orgId: deleteRedirectOrganizationId })
                          .link,
                        ignoreBlocker: true,
                        replace: true,
                      })

                      void Promise.resolve()
                        .then(() => deleteMutation.cleanupDeletedOrganization(row.original.id))
                        .catch(() => undefined)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <FloristLifecycleDialog
            organization={row.original}
            open={isLifecycleDialogOpen}
            onOpenChange={setIsLifecycleDialogOpen}
          />
        </>
      )
    },
  },
]

export const organizationIndexSortColumns = {
  name: {
    asc: 'name_asc',
    desc: 'name_desc',
    defaultSort: 'name_asc',
  },
  status: {
    asc: 'status_asc',
    desc: 'status_desc',
    defaultSort: 'status_asc',
  },
  domain: {
    asc: 'domain_asc',
    desc: 'domain_desc',
    defaultSort: 'domain_asc',
  },
  fsnClientId: {
    asc: 'fsn_client_id_asc',
    desc: 'fsn_client_id_desc',
    defaultSort: 'fsn_client_id_asc',
  },
  phone: {
    asc: 'phone_asc',
    desc: 'phone_desc',
    defaultSort: 'phone_asc',
  },
  email: {
    asc: 'email_asc',
    desc: 'email_desc',
    defaultSort: 'email_asc',
  },
  location: {
    asc: 'location_asc',
    desc: 'location_desc',
    defaultSort: 'location_asc',
  },
  created: {
    asc: 'created_asc',
    desc: 'created_desc',
    defaultSort: 'created_desc',
  },
}

function getLegacyOrganizationCell(columnKey) {
  return organizationColumns.find((column) => (column.accessorKey ?? column.id) === columnKey)?.cell
}

export function getOrganizationIndexColumns(formatPhone = (phone) => phone || '') {
  const StatusCell = getLegacyOrganizationCell('florist_status')
  const PhoneCell = getLegacyOrganizationCell('default_location.phone')
  const ActionsCell = getLegacyOrganizationCell('actions')

  return [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <OrganizationNameCell organization={row.original} emphasized />,
      meta: {
        label: 'Name',
        kind: 'primary',
        icon: ArrowDownAZ,
        sortable: organizationIndexSortColumns.name,
        link: 'overlay',
        exportValue: (organization) => organization.name,
      },
    },
    {
      id: 'status',
      accessorKey: 'florist_status',
      header: 'Status',
      cell: ({ row }) => (StatusCell ? <StatusCell row={row} /> : null),
      meta: {
        label: 'Status',
        kind: 'badge',
        icon: BadgeCheck,
        sortable: organizationIndexSortColumns.status,
        align: 'center',
        exportValue: getOrganizationStatusText,
      },
    },
    {
      id: 'domain',
      accessorKey: 'primary_domain',
      header: 'Primary Domain',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.primary_domain || ''}
        </span>
      ),
      meta: {
        label: 'Primary Domain',
        kind: 'text',
        icon: Globe2,
        sortable: organizationIndexSortColumns.domain,
        exportValue: (organization) => organization.primary_domain || '',
      },
    },
    {
      id: 'fsn_client_id',
      accessorKey: 'fsn_client_id',
      header: 'FSN',
      cell: ({ row }) => <FsnStatusIndicator clientId={row.original.fsn_client_id} />,
      meta: {
        label: 'FSN',
        exportLabel: 'FSN Client ID',
        kind: 'badge',
        icon: Network,
        sortable: organizationIndexSortColumns.fsnClientId,
        align: 'center',
        link: false,
        exportValue: (organization) => organization.fsn_client_id || '',
      },
    },
    {
      id: 'phone',
      accessorKey: 'default_location.phone',
      header: 'Phone',
      cell: ({ row }) => (PhoneCell ? <PhoneCell row={row} /> : null),
      meta: {
        label: 'Phone',
        kind: 'text',
        icon: Phone,
        sortable: organizationIndexSortColumns.phone,
        exportValue: (organization) =>
          organization.default_location?.phone
            ? formatPhone(
                organization.default_location.phone,
                organization.default_location.phone_country_code
              )
            : '',
      },
    },
    {
      id: 'email',
      accessorKey: 'default_location.email',
      header: 'Email',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.default_location?.email || ''}
        </span>
      ),
      meta: {
        label: 'Email',
        kind: 'text',
        icon: Mail,
        sortable: organizationIndexSortColumns.email,
        exportValue: (organization) => organization.default_location?.email || '',
      },
    },
    {
      id: 'location',
      accessorKey: 'default_location.city',
      header: 'Default Location',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {formatDefaultLocation(row.original.default_location)}
        </span>
      ),
      meta: {
        label: 'Default Location',
        kind: 'text',
        icon: MapPin,
        sortable: organizationIndexSortColumns.location,
        exportValue: (organization) => formatDefaultLocation(organization.default_location),
      },
    },
    {
      id: 'created',
      accessorKey: 'created_at',
      header: 'Date Created',
      cell: ({ row }) => (
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">
          {row.original.created_at ? dayjs(row.original.created_at).format('MMM D, YYYY') : ''}
        </span>
      ),
      meta: {
        label: 'Date Created',
        kind: 'date',
        icon: CalendarClock,
        sortable: organizationIndexSortColumns.created,
        exportValue: (organization) => organization.created_at,
        exportType: 'date',
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (ActionsCell ? <ActionsCell row={row} /> : null),
      meta: {
        label: 'Actions',
        kind: 'actions',
        link: false,
        align: 'end',
        headerClassName: 'w-12',
        cellClassName: 'w-12',
        exportable: false,
      },
    },
  ]
}

export function getOrganizationExportColumns(formatPhone = (phone) => phone || '') {
  const columnsById = new Map(
    getOrganizationIndexColumns(formatPhone).map((column) => [column.id, column])
  )

  return [
    columnsById.get('name'),
    columnsById.get('status'),
    columnsById.get('created'),
    createIndexTableExportColumn({
      id: 'live_at',
      label: 'Live At',
      type: 'date',
      value: (organization) =>
        formatOrganizationLifecycleDate(organization.live_at, organization.timezone),
    }),
    createIndexTableExportColumn({
      id: 'lost_at',
      label: 'Lost At',
      type: 'date',
      value: (organization) =>
        formatOrganizationLifecycleDate(organization.lost_at, organization.timezone),
    }),
    createIndexTableExportColumn({
      id: 'sales_rep',
      label: 'Sales Rep',
      value: (organization) => organization.sales_rep?.name || '',
    }),
    createIndexTableExportColumn({
      id: 'sales_rep_email',
      label: 'Sales Rep Email',
      value: (organization) => organization.sales_rep?.email || '',
    }),
    createIndexTableExportColumn({
      id: 'support_rep',
      label: 'Support Rep',
      value: (organization) => organization.support_rep?.name || '',
    }),
    columnsById.get('location'),
    columnsById.get('phone'),
    columnsById.get('email'),
    columnsById.get('domain'),
    columnsById.get('fsn_client_id'),
  ]
}
